"use client"

import { handleAddVariantToCart } from "@/components/custom/Product/AddCart"
import { useAppContext } from "@/lib/context/app-context"
import { useIframeEvent } from "@/context/IframeContext"
import { StoreProductVariant } from "@medusajs/types"
import { useRef, useState } from "react"
import { flyToCart } from "./flyToCart"

type State = "idle" | "loading" | "success"

export const BarebonesAddToCartButton = ({
  variant,
  title,
  locale,
  thumbnail,
  disabled,
  children,
  className,
}: {
  variant?: StoreProductVariant | null
  title: string
  locale: string
  thumbnail?: string | null
  disabled?: boolean
  children: React.ReactNode
  className?: string
}) => {
  const appContext = useAppContext()
  const iframeEvent = useIframeEvent()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [state, setState] = useState<State>("idle")

  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled || !variant || state === "loading") return

    setState("loading")
    const ok = await handleAddVariantToCart({
      variant,
      title,
      locale,
      appContext,
    })

    if (!ok) {
      setState("idle")
      return
    }

    iframeEvent.sentRefreshDataToIframe?.()
    setState("success")
    flyToCart(buttonRef.current, thumbnail)

    window.setTimeout(() => setState("idle"), 1400)
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      disabled={disabled || state === "loading"}
      onClick={onClick}
      className={`bb-atc relative inline-flex min-w-[7.5rem] items-center justify-center overflow-hidden rounded-full bg-[#2b2118] px-3 py-2 text-[11px] uppercase tracking-[0.08em] text-[#f7f4ef] disabled:opacity-40 ${
        state === "success" ? "bb-atc-success" : ""
      } ${className || ""}`}
    >
      <span
        className={`bb-atc-label transition-all duration-300 ${
          state === "idle"
            ? "translate-y-0 opacity-100"
            : "-translate-y-3 opacity-0"
        }`}
      >
        {children}
      </span>

      <span
        className={`pointer-events-none absolute inset-0 flex items-center justify-center transition-all duration-300 ${
          state === "loading"
            ? "translate-y-0 opacity-100"
            : "translate-y-3 opacity-0"
        }`}
        aria-hidden={state !== "loading"}
      >
        <span className="bb-atc-spinner h-3.5 w-3.5 rounded-full border-2 border-[#f7f4ef]/30 border-t-[#f7f4ef]" />
      </span>

      <span
        className={`pointer-events-none absolute inset-0 flex items-center justify-center transition-all duration-300 ${
          state === "success"
            ? "translate-y-0 scale-100 opacity-100"
            : "translate-y-3 scale-75 opacity-0"
        }`}
        aria-hidden={state !== "success"}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path
            d="M3.5 8.2 6.4 11l6.1-6.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  )
}
